import { ContentCount, BrandPlanTacticContents, BrandPlanTactic, CrosspostFrequency, BrandPlanContents } from 'types'
import {
  DEFAULT_CROSSPOST_CONTENTS,
  DEFAULT_EMAIL_CONTENTS,
  DEFAULT_FACEBOOK_CONTENTS,
  DEFAULT_GOOGLE_BUSINESS_PROFILE_CONTENTS,
  DEFAULT_INSTAGRAM_CONTENTS,
  DEFAULT_LINKEDIN_CONTENTS,
  DEFAULT_SEO_CONTENTS,
  DEFAULT_TIKTOK_CONTENTS,
  DEFAULT_TWITTER_CONTENTS,
  DEFAULT_VIDEO_CROSSPOST_CONTENTS,
  DEFAULT_YOUTUBE_CONTENTS,
} from 'constants/contentCountDefaults'
import {
  SOCIAL_MEDIA_TACTICS,
  SocialTactic,
  VIDEO_TACTICS,
} from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'

export const mergeDefaults = (
  defaults: readonly ContentCount[],
  contents: ContentCount[] | undefined,
  accountIds?: number[],
  isCrosspost: boolean = false,
): ContentCount[] => {
  const contentCounts = contents || []

  // Helper to find existing entry by type and optional account ID
  const findExisting = (type: string, accountId?: number): ContentCount | undefined => {
    if (accountId !== undefined) {
      return contentCounts.find((c) => c.type === type && c.account_ids?.includes(accountId))
    }
    // Find entry without account_ids or with empty account_ids array
    return contentCounts.find((c) => c.type === type && (!c.account_ids || c.account_ids.length === 0))
  }

  // Crosspost mode: single entry with account IDs
  if (isCrosspost) {
    // New shape: entries are discriminated by content_type — preserve all of them
    if (contentCounts.some((c) => c.content_type !== undefined)) {
      return contentCounts.map((existingItem) => {
        if (existingItem.account_ids && accountIds && accountIds.length > 0) {
          const validAccountIds = existingItem.account_ids.filter((id) => accountIds.includes(id))
          if (validAccountIds.length > 0) {
            return { ...existingItem, account_ids: validAccountIds }
          }
          const { account_ids, ...rest } = existingItem
          return rest
        }
        if (existingItem.account_ids) {
          return existingItem
        }
        return accountIds && accountIds.length > 0 ? { ...existingItem, account_ids: accountIds } : existingItem
      })
    }

    // Old shape: single entry per type, matched by type only
    return defaults.map((defaultItem) => {
      const existing = contentCounts.find((c) => c.type === defaultItem.type)
      const baseEntry = existing || defaultItem

      // Preserve existing account_ids, but filter to only include accounts that are valid for current tactics
      if (existing?.account_ids && accountIds && accountIds.length > 0) {
        const validAccountIds = existing.account_ids.filter((id) => accountIds.includes(id))
        if (validAccountIds.length > 0) {
          return { ...baseEntry, account_ids: validAccountIds }
        }
        // No valid accounts remaining - remove account_ids field
        const { account_ids, ...rest } = baseEntry
        return rest
      }

      // If existing has account_ids but no new accountIds provided, preserve existing
      if (existing?.account_ids) {
        return baseEntry
      }

      // No existing account_ids - use provided accountIds if available
      return accountIds && accountIds.length > 0 ? { ...baseEntry, account_ids: accountIds } : baseEntry
    })
  }

  // Multi-account mode: separate entry per account
  if (accountIds && accountIds.length > 1) {
    return defaults.flatMap((defaultItem) =>
      accountIds.map((accountId) => {
        const existing = findExisting(defaultItem.type, accountId)
        if (existing) return existing

        // Use default entry count if available
        const defaultEntry = findExisting(defaultItem.type)
        return {
          type: defaultItem.type,
          count: defaultEntry?.count ?? defaultItem.count,
          account_ids: [accountId],
        }
      }),
    )
  }

  // Single account mode: use defaults with account_ids
  if (accountIds && accountIds.length === 1) {
    return defaults.map((defaultItem) => {
      // First try to find entry with this account ID
      const existing = findExisting(defaultItem.type, accountIds[0]) || findExisting(defaultItem.type)
      return {
        ...(existing || defaultItem),
        account_ids: accountIds,
      }
    })
  }

  // No account mode: use defaults without account_ids, but preserve existing account_ids if present
  return defaults.flatMap((defaultItem) => {
    // First try to find entry without account_ids (backwards compatibility)
    const existing = findExisting(defaultItem.type)
    if (existing) return [existing]

    // Find ALL entries with this type (preserves all account-specific entries)
    const allExisting = contentCounts.filter((c) => c.type === defaultItem.type)
    if (allExisting.length > 0) return allExisting

    // No existing entries, return default
    return [defaultItem]
  })
}

export const mergeInstagramDefaults = (contents: ContentCount[] | undefined, accountIds?: number[]) =>
  mergeDefaults(DEFAULT_INSTAGRAM_CONTENTS, contents, accountIds)
export const mergeFacebookDefaults = (contents: ContentCount[] | undefined, accountIds?: number[]) =>
  mergeDefaults(DEFAULT_FACEBOOK_CONTENTS, contents, accountIds)
export const mergeTwitterDefaults = (contents: ContentCount[] | undefined, accountIds?: number[]) =>
  mergeDefaults(DEFAULT_TWITTER_CONTENTS, contents, accountIds)
export const mergeLinkedInDefaults = (contents: ContentCount[] | undefined, accountIds?: number[]) =>
  mergeDefaults(DEFAULT_LINKEDIN_CONTENTS, contents, accountIds)
export const mergeYoutubeDefaults = (contents: ContentCount[] | undefined, accountIds?: number[]) =>
  mergeDefaults(DEFAULT_YOUTUBE_CONTENTS, contents, accountIds)
export const mergeTiktokDefaults = (contents: ContentCount[] | undefined, accountIds?: number[]) =>
  mergeDefaults(DEFAULT_TIKTOK_CONTENTS, contents, accountIds)
export const mergeSeoDefaults = (contents: ContentCount[] | undefined, accountIds?: number[]) =>
  mergeDefaults(DEFAULT_SEO_CONTENTS, contents, accountIds)
export const mergeEmailDefaults = (contents: ContentCount[] | undefined, accountIds?: number[]) =>
  mergeDefaults(DEFAULT_EMAIL_CONTENTS, contents, accountIds)
export const mergeCrosspostDefaults = (contents: ContentCount[] | undefined, accountIds?: number[]) =>
  mergeDefaults(DEFAULT_CROSSPOST_CONTENTS, contents, accountIds, true)
export const mergeVideoCrosspostDefaults = (contents: ContentCount[] | undefined, accountIds?: number[]) =>
  mergeDefaults(DEFAULT_VIDEO_CROSSPOST_CONTENTS, contents, accountIds, true)
export const mergeGoogleBusinessProfileDefaults = (contents: ContentCount[] | undefined, accountIds?: number[]) =>
  mergeDefaults(DEFAULT_GOOGLE_BUSINESS_PROFILE_CONTENTS, contents, accountIds)

export type MergeAllContentsParams = {
  instagramContents?: BrandPlanTacticContents
  facebookContents?: BrandPlanTacticContents
  linkedinContents?: BrandPlanTacticContents
  twitterContents?: BrandPlanTacticContents
  tiktokContents?: BrandPlanTacticContents
  youtubeContents?: BrandPlanTacticContents
  seoContents?: BrandPlanTacticContents
  emailContents?: BrandPlanTacticContents
  crosspostContents?: BrandPlanTacticContents
  videoCrosspostContents?: BrandPlanTacticContents
  googleBusinessProfileContents?: BrandPlanTacticContents
  crosspostFrequency: CrosspostFrequency
  tactics: BrandPlanTactic[]
  instagramAccountIds?: number[]
  facebookAccountIds?: number[]
  linkedinAccountIds?: number[]
  twitterAccountIds?: number[]
  tiktokAccountIds?: number[]
  youtubeAccountIds?: number[]
  seoAccountIds?: number[]
  emailAccountIds?: number[]
  googleBusinessProfileAccountIds?: number[]
}

export type MergeAllContentsReturn = BrandPlanContents & {
  tactics: BrandPlanTactic[]
  crosspost_frequency: CrosspostFrequency
}

export const mergeAllPlatformContents = ({
  instagramContents,
  facebookContents,
  linkedinContents,
  twitterContents,
  tiktokContents,
  youtubeContents,
  seoContents,
  emailContents,
  crosspostContents,
  videoCrosspostContents,
  googleBusinessProfileContents,
  crosspostFrequency,
  tactics,
  instagramAccountIds,
  facebookAccountIds,
  linkedinAccountIds,
  twitterAccountIds,
  tiktokAccountIds,
  youtubeAccountIds,
  seoAccountIds,
  emailAccountIds,
  googleBusinessProfileAccountIds,
}: MergeAllContentsParams): MergeAllContentsReturn => {
  const crosspostTactics = tactics.filter((tactic) => SOCIAL_MEDIA_TACTICS.includes(tactic as SocialTactic))
  const videoCrosspostTactics = tactics.filter((tactic) => VIDEO_TACTICS.includes(tactic))

  const tacticAccountMap: Record<BrandPlanTactic, number[] | undefined> = {
    [BrandPlanTactic.Instagram]: instagramAccountIds,
    [BrandPlanTactic.Facebook]: facebookAccountIds,
    [BrandPlanTactic.LinkedIn]: linkedinAccountIds,
    [BrandPlanTactic.Twitter]: twitterAccountIds,
    [BrandPlanTactic.InstagramReel]: instagramAccountIds,
    [BrandPlanTactic.TikTok]: tiktokAccountIds,
    [BrandPlanTactic.YouTube]: youtubeAccountIds,
    [BrandPlanTactic.SEO]: seoAccountIds,
    [BrandPlanTactic.Email]: emailAccountIds,
    [BrandPlanTactic.MetaAds]: undefined,
    [BrandPlanTactic.GoogleBusinessProfile]: googleBusinessProfileAccountIds,
  }

  const crosspostAccountIds = crosspostTactics.flatMap((tactic) => tacticAccountMap[tactic] || [])
  const videoCrosspostAccountIds = videoCrosspostTactics.flatMap((tactic) => tacticAccountMap[tactic] || [])

  return {
    instagram_contents: mergeInstagramDefaults(instagramContents, instagramAccountIds),
    facebook_contents: mergeFacebookDefaults(facebookContents, facebookAccountIds),
    linkedin_contents: mergeLinkedInDefaults(linkedinContents, linkedinAccountIds),
    twitter_contents: mergeTwitterDefaults(twitterContents, twitterAccountIds),
    tiktok_contents: mergeTiktokDefaults(tiktokContents, tiktokAccountIds),
    youtube_contents: mergeYoutubeDefaults(youtubeContents, youtubeAccountIds),
    seo_contents: mergeSeoDefaults(seoContents, seoAccountIds),
    email_contents: mergeEmailDefaults(emailContents, emailAccountIds),
    crosspost_contents: mergeCrosspostDefaults(crosspostContents, crosspostAccountIds),
    video_crosspost_contents: mergeVideoCrosspostDefaults(videoCrosspostContents, videoCrosspostAccountIds),
    google_my_business_contents: mergeGoogleBusinessProfileDefaults(
      googleBusinessProfileContents,
      googleBusinessProfileAccountIds,
    ),
    crosspost_frequency: ['always', 'never'].includes(crosspostFrequency) ? crosspostFrequency : 'always',
    tactics,
  }
}
